<script setup>
import { computed, ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import authAPI from 'dashboard/api/auth';
import AnalyticsHelper from 'dashboard/helper/AnalyticsHelper';
import { SESSION_EVENTS } from 'dashboard/helper/AnalyticsHelper/events';
import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import SessionCard from './SessionCard.vue';

const { t } = useI18n();
const sessions = ref([]);
const loading = ref(false);
const sessionsDialogRef = ref(null);

const activeSessions = computed(() =>
  sessions.value.filter(session => session.active !== false)
);
const inactiveSessions = computed(() =>
  sessions.value.filter(session => session.active === false)
);
const visibleSessions = computed(() => activeSessions.value.slice(0, 3));
const hasMoreSessions = computed(() => sessions.value.length > 3);

const fetchSessions = async () => {
  loading.value = true;
  try {
    const { data } = await authAPI.getSessions();
    sessions.value = data;
  } catch {
    useAlert(t('PROFILE_SETTINGS.FORM.SESSIONS_SECTION.FETCH_ERROR'));
  } finally {
    loading.value = false;
  }
};

const revokeSession = async session => {
  try {
    await authAPI.revokeSession(session.id);
    sessions.value = sessions.value.map(item =>
      item.id === session.id ? { ...item, active: false } : item
    );
    AnalyticsHelper.track(SESSION_EVENTS.REVOKED_FROM_PROFILE);
    useAlert(t('PROFILE_SETTINGS.FORM.SESSIONS_SECTION.REVOKE_SUCCESS'));
  } catch {
    useAlert(t('PROFILE_SETTINGS.FORM.SESSIONS_SECTION.REVOKE_ERROR'));
  }
};

onMounted(fetchSessions);
</script>

<template>
  <div class="flex flex-col gap-3">
    <SessionCard
      v-for="session in visibleSessions"
      :key="session.id"
      :session="session"
      @revoke="revokeSession"
    />

    <div v-if="hasMoreSessions" class="flex justify-end pt-1">
      <Button
        type="button"
        faded
        sm
        icon="i-lucide-monitor-smartphone"
        :label="$t('PROFILE_SETTINGS.FORM.SESSIONS_SECTION.VIEW_ALL')"
        @click="sessionsDialogRef?.open()"
      />
    </div>

    <Dialog
      ref="sessionsDialogRef"
      width="2xl"
      overflow-y-auto
      :title="$t('PROFILE_SETTINGS.FORM.SESSIONS_SECTION.ALL_TITLE')"
      :description="$t('PROFILE_SETTINGS.FORM.SESSIONS_SECTION.ALL_NOTE')"
      :show-cancel-button="false"
      :confirm-button-label="$t('PROFILE_SETTINGS.FORM.SESSIONS_SECTION.CLOSE')"
      @confirm="sessionsDialogRef?.close()"
    >
      <div class="flex flex-col gap-6">
        <section class="flex flex-col gap-3">
          <div class="flex items-center justify-between">
            <h4 class="mb-0 text-sm font-semibold text-n-slate-12">
              {{ $t('PROFILE_SETTINGS.FORM.SESSIONS_SECTION.ACTIVE_SESSIONS') }}
            </h4>
            <span class="text-xs text-n-slate-10">
              {{ activeSessions.length }}
            </span>
          </div>
          <SessionCard
            v-for="session in activeSessions"
            :key="session.id"
            :session="session"
            show-status
            @revoke="revokeSession"
          />
        </section>

        <section v-if="inactiveSessions.length" class="flex flex-col gap-3">
          <div class="flex items-center justify-between">
            <h4 class="mb-0 text-sm font-semibold text-n-slate-12">
              {{
                $t('PROFILE_SETTINGS.FORM.SESSIONS_SECTION.INACTIVE_SESSIONS')
              }}
            </h4>
            <span class="text-xs text-n-slate-10">
              {{ inactiveSessions.length }}
            </span>
          </div>
          <SessionCard
            v-for="session in inactiveSessions"
            :key="session.id"
            :session="session"
            show-status
          />
        </section>
      </div>
    </Dialog>
  </div>
</template>
